// dashboard/src/pages/apps-setup/KeystoreFingerprint.tsx
// SHA-1 / SHA-256 of the signing certificate, with copy buttons: what Play
// Console, Firebase and Google sign-in ask for when an app is published.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import { getKeystoreFingerprint } from '../../api/app-builds';

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-content-subtle text-xs w-14 shrink-0">{label}</span>
      <code className="text-content text-xs break-all flex-1 min-w-0">{value}</code>
      <button
        type="button"
        onClick={copy}
        className="px-2.5 py-1 text-xs rounded-md border border-border text-content-muted hover:bg-surface-muted"
      >
        {copied ? 'Copied' : `Copy ${label}`}
      </button>
    </div>
  );
}

export function KeystoreFingerprint({ appId }: { appId: string }) {
  const q = useQuery({
    queryKey: ['keystore-fingerprint', appId],
    queryFn: () => getKeystoreFingerprint(appId),
    retry: false,
    staleTime: Infinity,
  });
  if (q.isLoading) return <p className="text-content-subtle text-xs">Reading certificate fingerprint…</p>;
  if (q.error) return <p className="text-red-500 text-xs">Certificate fingerprint: {(q.error as ApiError).message}</p>;
  if (!q.data) return null;
  return (
    <div className="bg-surface-muted rounded-md p-3 space-y-1.5">
      <CopyRow label="SHA-1" value={q.data.sha1} />
      <CopyRow label="SHA-256" value={q.data.sha256} />
    </div>
  );
}
